import { API } from 'aws-amplify';
import { apiGateway } from '../../config';
import { store } from '../';
import { showNotification } from './notification';

const Gateway = apiGateway.find((g) => g.name === 'client');

export const SET_FASTEN_CONNECTIONS = 'SET_FASTEN_CONNECTIONS'; // replace array for project
export const SET_FASTEN_SYNCING = 'SET_FASTEN_SYNCING';
export const SET_FASTEN_STATUS = 'SET_FASTEN_STATUS';
export const SET_FASTEN_EVENTS = 'SET_FASTEN_EVENTS';
export const SET_FASTEN_ERROR = 'SET_FASTEN_ERROR';
export const FASTEN_DISCONNECTED = 'FASTEN_DISCONNECTED';

export const createFastenConnection = (project_id, org_connection_id) => async (dispatch) => {
  const state = store.getState();
  const account_id = state.session.account.account_id;
  const cognito_id = state.authentication.user.cognito_id;
  return API.post(Gateway.name, `/fasten/create-connection/${project_id}/${account_id}/${cognito_id}`, {
    headers: { Authorization: state.session.token },
    body: { org_connection_id }
  }).then(async (res) => {
    if (res.success) {
      // After creating a connection, reload all connections to get the complete list
      await dispatch(loadFastenConnections(project_id));
      dispatch(showNotification('success', 'Health data connection saved. Starting sync...'));
    } else {
      dispatch(showNotification('error', 'Could not save health data connection.'));
    }
    return res;
  }).catch((e) => {
    dispatch(showNotification('error', 'Failed creating health data connection.'));
    return { success: false, error: e };
  });
};

export const triggerFastenExport = (org_connection_id, project_id) => async (dispatch) => {
  const state = store.getState();
  const account_id = state.session.account.account_id;
  const cognito_id = state.authentication.user.cognito_id;
  dispatch({ type: SET_FASTEN_SYNCING, payload: true });
  // Optimistic: mark target connection exporting immediately for snappy UI feedback
  const existing = state.fasten.connections[project_id];
  if (Array.isArray(existing)) {
    const patched = existing.map(c => c.org_connection_id === org_connection_id ? { ...c, status: 'exporting', error_message: null } : c);
    dispatch({ type: SET_FASTEN_CONNECTIONS, payload: { project_id, connections: patched } });
  } else if (existing && existing.org_connection_id === org_connection_id) {
    dispatch({ type: SET_FASTEN_CONNECTIONS, payload: { project_id, connections: [{ ...existing, status: 'exporting', error_message: null }] } });
  }
  return API.post(Gateway.name, `/fasten/trigger-export/${project_id}/${account_id}/${cognito_id}`, {
    headers: { Authorization: state.session.token },
    body: { org_connection_id }
  }).then((res) => {
    if (!res.success) {
      dispatch(showNotification('error', 'Failed to start sync.'));
      // Revert optimistic change by reloading connections (ensures authoritative state)
      dispatch(loadFastenConnections(project_id));
    }
    dispatch({ type: SET_FASTEN_SYNCING, payload: false });
    return res;
  }).catch((e) => {
    dispatch(showNotification('error', 'Failed to start sync.'));
    // Revert optimistic change
    dispatch(loadFastenConnections(project_id));
    dispatch({ type: SET_FASTEN_SYNCING, payload: false });
    return { success: false, error: e };
  });
};

export const loadFastenConnections = (project_id) => async (dispatch) => {
  const state = store.getState();
  const account_id = state.session.account.account_id;
  const cognito_id = state.authentication.user.cognito_id;
  return API.get(Gateway.name, `/fasten/get-connections/${project_id}/${account_id}/${cognito_id}`, {
    headers: { Authorization: state.session.token }
  }).then((res) => {
    if (res.success) {
      dispatch({ type: SET_FASTEN_CONNECTIONS, payload: { project_id, connections: res.payload || [] } });
    }
    return res;
  }).catch((e) => ({ success: false, error: e }));
};

export const fetchFastenExportStatus = (org_connection_id) => async (dispatch) => {
  const state = store.getState();
  const account_id = state.session.account.account_id;
  const cognito_id = state.authentication.user.cognito_id;
  return API.post(Gateway.name, `/fasten/get-export-status/${account_id}/${cognito_id}`, {
    headers: { Authorization: state.session.token },
    body: { org_connection_id }
  }).then((res) => {
    if (res.success) dispatch({ type: SET_FASTEN_STATUS, payload: res.payload });
    return res;
  }).catch((e) => ({ success: false, error: e }));
};

export const loadFastenEvents = (project_id) => async (dispatch) => {
  const state = store.getState();
  const account_id = state.session.account.account_id;
  const cognito_id = state.authentication.user.cognito_id;
  return API.get(Gateway.name, `/fasten/get-events/${project_id}/${account_id}/${cognito_id}`, {
    headers: { Authorization: state.session.token }
  }).then((res) => {
    if (res.success) dispatch({ type: SET_FASTEN_EVENTS, payload: res.payload });
    return res;
  }).catch((e) => ({ success: false, error: e }));
};

export const retryFastenExport = (org_connection_id, project_id) => async (dispatch) => {
  // Simply re-trigger export and clear error
  dispatch({ type: SET_FASTEN_ERROR, payload: null });
  return dispatch(triggerFastenExport(org_connection_id, project_id));
};

// Resume pending (partial) export: only available to eleplan-admin via UI
export const resumeFastenPending = (org_connection_id, project_id) => async (dispatch) => {
  const state = store.getState();
  const account_id = state.session.account.account_id;
  const cognito_id = state.authentication.user.cognito_id;
  dispatch({ type: SET_FASTEN_SYNCING, payload: true });
  return API.post(Gateway.name, `/fasten/resume-pending/${project_id}/${account_id}/${cognito_id}`, {
    headers: { Authorization: state.session.token },
    body: { org_connection_id }
  }).then(async (res) => {
    if (res.success) {
      // Optimistic: mark connection exporting again
      const existing = state.fasten.connections[project_id];
      if (Array.isArray(existing)) {
        const patched = existing.map(c => c.org_connection_id === org_connection_id ? { ...c, status: 'exporting', error_message: null } : c);
        dispatch({ type: SET_FASTEN_CONNECTIONS, payload: { project_id, connections: patched } });
      }
      dispatch(showNotification('success', 'Resume export triggered.'));    
    } else {
      dispatch(showNotification('error', 'Failed to resume export.'));
    }
    dispatch({ type: SET_FASTEN_SYNCING, payload: false });
    return res;
  }).catch(e => {
    dispatch(showNotification('error', 'Failed to resume export.'));
    dispatch({ type: SET_FASTEN_SYNCING, payload: false });
    return { success: false, error: e };
  });
};

export const disconnectFasten = (project_id, org_connection_id = null) => async (dispatch) => {
  const state = store.getState();
  const account_id = state.session.account.account_id;
  const cognito_id = state.authentication.user.cognito_id;
  return API.post(Gateway.name, `/fasten/disconnect/${project_id}/${account_id}/${cognito_id}`, {
    headers: { Authorization: state.session.token }, body: { org_connection_id }
  }).then((res) => {
    if (res.success) {
      const existing = state.fasten.connections[project_id];
      if (Array.isArray(existing)) {
        const updated = existing.map(c => (org_connection_id ? (c.org_connection_id === org_connection_id ? { ...c, status: 'revoked', error_message: 'manual_disconnect' } : c) : { ...c, status: 'revoked', error_message: 'manual_disconnect' }));
        dispatch({ type: SET_FASTEN_CONNECTIONS, payload: { project_id, connections: updated } });
      } else if (existing) {
        // Convert single connection to array format
        dispatch({ type: SET_FASTEN_CONNECTIONS, payload: { project_id, connections: [{ ...existing, status: 'revoked', error_message: 'manual_disconnect' }] } });
      }
      dispatch(showNotification('success', 'Health data connection disconnected.'));
    } else {
      dispatch(showNotification('error', 'Failed to disconnect.'));
    }
    return res;
  }).catch((e) => {
    dispatch(showNotification('error', 'Failed to disconnect.'));
    return { success: false, error: e };
  });
};

// Admin-only: backfill FHIR events from an S3 NDJSON export or prefix
export const backfillFastenFhirEvents = (project_id, { rawExportKey, dryRun = true, startDate, endDate, sampleLimit = 3 }) => async (dispatch) => {
  const state = store.getState();
  const account_id = state.session.account.account_id;
  const cognito_id = state.authentication.user.cognito_id;
  if (!rawExportKey) return { success: false, message: 'Missing rawExportKey' };
  return API.post(Gateway.name, `/fasten/backfill-fhir-events/${project_id}/${account_id}/${cognito_id}`, {
    headers: { Authorization: state.session.token },
    body: {
      rawExportKey: rawExportKey.trim(),
      dryRun: !!dryRun,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      sampleLimit
    }
  }).then(res => {
    if (!res.success) {
      dispatch(showNotification('error', res.message || 'Backfill failed'));
    } else if (dryRun) {
      dispatch(showNotification('info', 'Backfill simulation complete'));
    } else {
      dispatch(showNotification('success', 'Backfill completed'));
      // Reload connections & events to reflect newly ingested data
      dispatch(loadFastenConnections(project_id));
      dispatch(loadFastenEvents(project_id));
    }
    return res;
  }).catch(e => {
    dispatch(showNotification('error', 'Backfill request failed'));
    return { success: false, error: e };
  });
};

